import React, { useEffect, useMemo, useRef } from 'react';
import { Loader, LoaderRef } from '@servicetitan/web-components';
import softPhoneModule from '@modules/SoftPhoneModule';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import environment from '@lib/environment';
import useCallingDisabled from '@lib/hooks/useCallingDisabled';
import FeatureFlagModule, {
  FeatureFlagKeys,
  useFeatureFlags,
} from '@modules/FeatureFlagModule';
import {
  SoftPhoneFeatureToggles,
  WebphoneConfig,
  WebphoneUnloadWarningOptions,
  WebphoneUserContext,
} from '../types';
import { hasValidSeat } from '@constants/user';

/**
 * SoftPhoneLoader component is responsible for loading the soft phone MFE and initializing the web phone service with the event bus
 * The event bus is used to communicate between the soft phone MFE and the contact center frontend.
 */
const SoftPhoneLoader = () => {
  const { user, token } = useAuth();
  const { callingDisabled } = useCallingDisabled();
  const {
    [FeatureFlagKeys.featureGracefullyHandleMicrophoneDisconnect]:
      hasGracefullyHandleMicrophoneDisconnect,
    [FeatureFlagKeys.rtcWebphoneEnableCallConnectionMonitoring]:
      rtcWebphoneEnableCallConnectionMonitoring,
    [FeatureFlagKeys.featurePreventMultipleRegistrations]:
      featurePreventMultipleRegistrations,
    [FeatureFlagKeys.featureRtcWebphoneHandlesFetchingJwt]:
      featureRtcWebphoneHandlesFetchingJwt,
    [FeatureFlagKeys.fixDisablePopUpOnClose]: fixDisablePopUpOnClose,
    [FeatureFlagKeys.featureEnableSoftphoneMfeDebugging]:
      featureEnableSoftphoneMfeDebugging,
    [FeatureFlagKeys.featureWebphoneServerConnectionMonitoring]:
      featureWebphoneServerConnectionMonitoring,
  } = useFeatureFlags();

  const [isSoftPhoneMounted, setIsSoftPhoneMounted] = React.useState(false);
  const softPhoneLoaderRef = useRef<LoaderRef>(null);

  // Conditional mounting logic based on feature flag
  const hasWebPhoneToken = !!user?.webPhoneToken;
  const hasTelecomConfig = !!user?.org.telecomConfig;

  const shouldMount = featureRtcWebphoneHandlesFetchingJwt
    ? hasTelecomConfig && hasValidSeat(user?.seatLevel)
    : hasWebPhoneToken;
  /**
   * @deprecated with CCP-6116
   */
  const LEGACY_isSoftPhoneMounted = shouldMount && !callingDisabled;

  const featureToggles: SoftPhoneFeatureToggles = useMemo(
    () => ({
      enableCallMonitoring: !!rtcWebphoneEnableCallConnectionMonitoring,
      preventDuplicateWebphoneRegistrations:
        !!featurePreventMultipleRegistrations,
      enableServerConnectionMonitoring:
        !!featureWebphoneServerConnectionMonitoring,
      enableAutomaticConnectionRecovery:
        !!featureWebphoneServerConnectionMonitoring,
    }),
    [
      rtcWebphoneEnableCallConnectionMonitoring,
      featurePreventMultipleRegistrations,
      featureWebphoneServerConnectionMonitoring,
    ],
  );

  // Conditional data structure based on feature flag
  const webphoneConfig: WebphoneConfig = useMemo(() => {
    const isDebugLogging = !!featureEnableSoftphoneMfeDebugging;

    const baseConfig = {
      debugLogging: isDebugLogging,
      pcsJwt: token,
      pcsApiUrl: user?.org.telecomConfig?.pcsBaseAddress,
    };

    // If feature flag is disabled, include legacy jwt and sipUri
    if (!featureRtcWebphoneHandlesFetchingJwt) {
      return {
        ...baseConfig,
        jwt: user?.webPhoneToken ?? '',
        sipUri: user?.org.telecomConfig?.sipBaseAddress,
      };
    }

    // If feature flag is enabled, only return base config (MFE handles JWT)
    return baseConfig;
  }, [
    token,
    user?.webPhoneToken,
    user?.org.telecomConfig?.pcsBaseAddress,
    user?.org.telecomConfig?.sipBaseAddress,
    featureRtcWebphoneHandlesFetchingJwt,
    featureEnableSoftphoneMfeDebugging,
  ]);

  const userContext: WebphoneUserContext | undefined = useMemo(() => {
    if (!user) {
      return undefined;
    }
    return {
      pcsUserId: user.contactCenterUserId ?? '',
      pcsAccountId: user.accountId,
      pcsContactCenterId: user.contactCenterId,
    };
  }, [user]);

  // latch the soft phone mount state
  React.useEffect(() => {
    // Once the soft phone is mounted, it becomes initialized globally.
    // If we unload it in react, event bus handler logic in the MFE will break.
    if (shouldMount && !callingDisabled) {
      setIsSoftPhoneMounted(true);
    }
  }, [shouldMount, callingDisabled]);

  // init/uninit the soft phone service based on the shouldLoadSoftphone flag
  useEffect(() => {
    if (!isSoftPhoneMounted) return undefined;
    const eventBus = softPhoneLoaderRef.current;
    // initialize the soft phone with the event bus
    softPhoneModule.initializeWebPhoneService(eventBus ?? undefined);

    if (
      FeatureFlagModule.isEnabled(
        FeatureFlagKeys.featureGracefullyHandleMicrophoneDisconnect,
      )
    ) {
      return () => {
        // edge case, consumer unmounts the soft phone loader
        softPhoneModule.uninitializeWebPhoneService();
      };
    }
    return undefined;
  }, [isSoftPhoneMounted]);

  if (hasGracefullyHandleMicrophoneDisconnect && !isSoftPhoneMounted)
    return null;
  if (!hasGracefullyHandleMicrophoneDisconnect && !LEGACY_isSoftPhoneMounted)
    return null;
  return (
    <div className='d-n' data-testid='soft-phone-loader'>
      <Loader
        ref={softPhoneLoaderRef}
        src={environment.SOFTPHONE_MFE_URL}
        data={{
          ...webphoneConfig,
          featureToggles: featureToggles,
          userContext: userContext,
          ...(fixDisablePopUpOnClose
            ? {
                webphoneUnloadWarningOptions:
                  WebphoneUnloadWarningOptions.OnActiveCallOnly,
              }
            : {}),
        }}
      />
    </div>
  );
};

export default SoftPhoneLoader;
